"use client";

import { TZDate } from "@date-fns/tz";
import {
  differenceInHours,
  differenceInMinutes,
  isThisWeek,
  isToday,
  isTomorrow,
} from "date-fns";
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Flag,
  Gauge,
  Link2,
  MapPin,
  Quote,
  Send,
  Share2,
  Trash2,
  Users,
  Wallet,
} from "lucide-react";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import type { ActivitySessionFeedItem } from "@/actions/activity-session";
import { SessionScene } from "@/components/activity-sessions/session-scene";
import { FeedActionRow } from "@/components/feed/feed-action-row";
import {
  FEED_X_BODY,
  FeedCardHeader,
} from "@/components/feed/feed-card-header";
import { RegularPostDrawer } from "@/components/feed/regular-post-drawer";
import { ReportFeedItemDialog } from "@/components/feed/report-feed-item-dialog";
import {
  CommentsSheet,
  type CommentsSheetViewer,
} from "@/components/posts/comments-sheet";
import { ExpandablePostText } from "@/components/posts/expandable-post-text";
import { LikesSheet } from "@/components/posts/likes-sheet";
import { SendToChatDrawer } from "@/components/share/send-to-chat-drawer";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { BackAwareLink } from "@/components/ui/back-aware-link";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import { useToggleActivitySessionLikeMutation } from "@/hooks/use-activity-session-likes";
import { useHrefWithFrom } from "@/hooks/use-current-href";
import {
  useDeleteFeedItemMutation,
  useTrackFeedShareMutation,
} from "@/hooks/use-feed-share";
import { displaySessionTitle } from "@/lib/activity-sessions/display-title";
import { formatEventDate } from "@/lib/dates/event-display";
import { formatRelativeTimeCompact } from "@/lib/dates/relative-time";
import { APP_DEFAULT_TIME_ZONE } from "@/lib/dates/zoned-input";
import type { FeedEntityQuoteTarget } from "@/lib/feed/feed-entity-quote";
import { getIconComponent, type IconType } from "@/lib/icon-registry";
import { captureCurrentRouteScroll } from "@/lib/navigation-scroll";
import { absolutePublicAppUrl } from "@/lib/public-app-url";
import { cn, formatMinorCurrency } from "@/lib/utils";

type ActivitySessionCardProps = {
  event: ActivitySessionFeedItem;
  viewer?: CommentsSheetViewer;
  embedded?: boolean;
};

// Smart time display for status badge
const getSmartTimeLabel = (
  startTime: Date,
  status: string,
): { label: string; icon: "clock" | "live" | "check" } => {
  const now = new Date();

  if (status === "completed") {
    return { label: "Completed", icon: "check" };
  }

  if (status === "in_progress") {
    return { label: "Live Now", icon: "live" };
  }

  if (status === "cancelled") {
    return { label: "Cancelled", icon: "check" };
  }

  // Upcoming - show smart countdown
  const minutesUntil = differenceInMinutes(startTime, now);
  const hoursUntil = differenceInHours(startTime, now);

  if (minutesUntil <= 0) {
    return { label: "Happening now", icon: "live" };
  }

  if (minutesUntil < 60) {
    return { label: `In ${minutesUntil}m`, icon: "clock" };
  }

  if (
    hoursUntil < 24 &&
    isToday(new TZDate(startTime, APP_DEFAULT_TIME_ZONE))
  ) {
    return {
      label: `Today ${formatEventDate(startTime, "h:mm a")}`,
      icon: "clock",
    };
  }

  if (isTomorrow(new TZDate(startTime, APP_DEFAULT_TIME_ZONE))) {
    return {
      label: `Tomorrow ${formatEventDate(startTime, "h:mm a")}`,
      icon: "clock",
    };
  }

  if (isThisWeek(new TZDate(startTime, APP_DEFAULT_TIME_ZONE))) {
    return { label: formatEventDate(startTime, "EEE h:mm a"), icon: "clock" };
  }

  return { label: formatEventDate(startTime, "MMM d"), icon: "clock" };
};

// The status pill sits on the card surface (beside "N of M joined"), off the
// scene: over the art a long label like "Tomorrow 6:00 pm" would run into the
// scene's centred activity label.
const STATUS_TONE = {
  upcoming: "bg-brand/10 text-brand dark:bg-brand/20 dark:text-violet-200",
  in_progress: "bg-warning/12 text-warning dark:bg-warning/20",
  completed: "bg-success/12 text-success dark:bg-success/20",
  cancelled: "bg-error/10 text-error dark:bg-error/20",
} as const;

const VISIBILITY_LABEL: Record<string, string> = {
  public: "Public",
  buddies: "Buddies only",
  close_friends: "Close friends",
  private: "Invite only",
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

function formatScoreLine(scoreData: Record<string, unknown> | null) {
  if (!scoreData) return null;

  const matchScore = isRecord(scoreData.matchScore)
    ? (scoreData.matchScore as Record<string, unknown>)
    : null;

  const resolveNumeric = (value: unknown) =>
    typeof value === "number" && Number.isFinite(value) ? value : null;

  const pickPair = (
    source: Record<string, unknown> | null,
    leftKey: string,
    rightKey: string,
  ) => {
    if (!source) return null;
    const left = resolveNumeric(source[leftKey]);
    const right = resolveNumeric(source[rightKey]);
    if (left == null || right == null) return null;
    return { left, right };
  };

  const pair =
    pickPair(matchScore, "yourSets", "opponentSets") ??
    pickPair(matchScore, "yourGames", "opponentGames") ??
    pickPair(matchScore, "yourFrames", "opponentFrames") ??
    pickPair(matchScore, "yourGoals", "opponentGoals") ??
    pickPair(matchScore, "yourPoints", "opponentPoints") ??
    pickPair(matchScore, "yourScore", "opponentScore") ??
    pickPair(scoreData, "team1Score", "team2Score");

  if (pair) {
    const detailsKey = Array.isArray(scoreData.sets)
      ? "sets"
      : Array.isArray(scoreData.games)
        ? "games"
        : Array.isArray(scoreData.frames)
          ? "frames"
          : null;
    const details = detailsKey
      ? (scoreData[detailsKey] as unknown[])
          .map((raw) => {
            if (!isRecord(raw)) return null;
            const left =
              resolveNumeric(raw.yourScore) ?? resolveNumeric(raw.team1Score);
            const right =
              resolveNumeric(raw.opponentScore) ??
              resolveNumeric(raw.team2Score);
            if (left == null || right == null) return null;
            return `${left}-${right}`;
          })
          .filter((item): item is string => item !== null)
      : [];

    return [
      `Team 1 ${pair.left}-${pair.right} Team 2`,
      details.length > 0 ? details.join(", ") : null,
    ]
      .filter(Boolean)
      .join(" • ");
  }

  const yourInnings = isRecord(scoreData.yourInnings)
    ? (scoreData.yourInnings as Record<string, unknown>)
    : null;
  const opponentInnings = isRecord(scoreData.opponentInnings)
    ? (scoreData.opponentInnings as Record<string, unknown>)
    : null;
  const cricketPair =
    yourInnings && opponentInnings
      ? pickPair(
          {
            yourRuns: yourInnings.runs,
            opponentRuns: opponentInnings.runs,
          },
          "yourRuns",
          "opponentRuns",
        )
      : null;

  if (cricketPair) {
    return `Team 1 ${cricketPair.left}-${cricketPair.right} Team 2`;
  }

  const winnerTeam = scoreData.winnerTeam;
  if (
    winnerTeam === "team1" ||
    winnerTeam === "team2" ||
    winnerTeam === "draw"
  ) {
    return winnerTeam === "draw"
      ? "Draw"
      : `Winner: ${winnerTeam === "team1" ? "Team 1" : "Team 2"}`;
  }

  return null;
}

export function ActivitySessionCard({
  event,
  viewer,
  embedded = false,
}: ActivitySessionCardProps) {
  const router = useRouter();
  const [isCommentsOpen, setCommentsOpen] = useState(false);
  const [isLikesOpen, setLikesOpen] = useState(false);
  const [isActionsOpen, setActionsOpen] = useState(false);
  const [isReportOpen, setReportOpen] = useState(false);
  const [isQuoteOpen, setQuoteOpen] = useState(false);
  const [isDeleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isSendInChatOpen, setSendInChatOpen] = useState(false);

  const toggleLikeMutation = useToggleActivitySessionLikeMutation();
  const trackShareMutation = useTrackFeedShareMutation();
  const deleteFeedItemMutation = useDeleteFeedItemMutation();

  // Local state for optimistic updates
  const [likeCount, setLikeCount] = useState(event.likeCount);
  const [isLiked, setIsLiked] = useState(event.isLikedByViewer);
  const [commentCount, setCommentCount] = useState(event.commentCount);
  const [shareCount, setShareCount] = useState(event.shareCount);

  const IconComponent = getIconComponent(event.activityIcon as IconType);
  const isCreatorBlocked = Boolean(event.creator.isBlocked);
  const creatorHandle = event.creator.username;
  const isOwner = Boolean(viewer?.id && viewer.id === event.creator.id);
  const timeAgo = formatRelativeTimeCompact(new Date(event.createdAt));
  const isRetroResultEntry = event.entryType === "retro_result";
  const smartTime = getSmartTimeLabel(new Date(event.startTime), event.status);
  const isDisputedSession = event.scoreVerificationStatus === "disputed";
  const scoreData = event.scoreSummary?.scoreData ?? null;
  const _scoreLine = formatScoreLine(scoreData);
  const resultStateLabel = (() => {
    if (event.scoreVerificationStatus === "verified") return "Verified";
    if (event.scoreVerificationStatus === "disputed") return "Disputed";
    if (event.scoreVerificationStatus === "expired") return "Expired";
    return "Pending";
  })();
  const showDescription = Boolean(
    event.description &&
      (!isRetroResultEntry || event.description !== "Retro result entry"),
  );
  const hasDisplayLocation = Boolean(
    (event.locationName || event.address) &&
      event.locationName !== "Location not set" &&
      event.address !== "Location not set",
  );
  const statusBadgeLabel = isRetroResultEntry
    ? resultStateLabel
    : isDisputedSession
      ? "Disputed"
      : smartTime.label;
  const priceLabel =
    event.pricingType === "paid"
      ? `${event.splitPricing === "divide_total" ? "Up to " : ""}${formatMinorCurrency(
          event.pricePerPersonAmountMinor,
          event.currency,
        )} / person`
      : "Free";
  const quoteTarget = {
    entityType: "activity_session",
    entityId: event.id,
    item: event,
  } satisfies FeedEntityQuoteTarget;
  const skillRequirementLabel =
    event.minSkillLevel != null && event.maxSkillLevel != null
      ? event.minSkillLevel === event.maxSkillLevel
        ? `Skill ${event.minSkillLevel}`
        : `Skill ${event.minSkillLevel}-${event.maxSkillLevel}`
      : null;
  const isUpcoming = event.status === "upcoming";
  const hasPendingRequest = Boolean(event.viewerJoinRequest);
  const canJoinDirect =
    isUpcoming &&
    !event.isJoinedByViewer &&
    !event.requiresApproval &&
    !hasPendingRequest &&
    event.participantCount < event.maxParticipants;
  const canRequestToJoin =
    isUpcoming &&
    !event.isJoinedByViewer &&
    event.requiresApproval &&
    event.participantCount < event.maxParticipants &&
    !hasPendingRequest;
  const ctaLabel = (() => {
    if (isRetroResultEntry) return "View Result";
    if (event.isJoinedByViewer) return "View Session";
    if (hasPendingRequest) return "Request Pending";
    if (canRequestToJoin) return "Request to Join";
    if (canJoinDirect) return "Join Session";
    return "View Session";
  })();
  const ctaHref =
    hasPendingRequest && event.viewerJoinRequest
      ? `/membership-requests/session/${event.viewerJoinRequest.id}`
      : `/sessions/${event.id}`;
  const sessionHref = `/sessions/${event.id}`;
  const sessionHrefWithFrom = useHrefWithFrom(sessionHref);
  const startDate = new Date(event.startTime);
  const endDate = new Date(event.endTime);
  const displayTitle = displaySessionTitle(event.title);
  const timeRangeLabel = `${formatEventDate(startDate, "EEE, d MMM")} · ${formatEventDate(
    startDate,
    "h:mm",
  )}–${formatEventDate(endDate, "h:mm a").toLowerCase()}`;
  const spotsLeft = Math.max(0, event.maxParticipants - event.participantCount);
  const showCta =
    isRetroResultEntry ||
    canJoinDirect ||
    canRequestToJoin ||
    hasPendingRequest ||
    (isUpcoming && event.isJoinedByViewer);
  const statusTone: keyof typeof STATUS_TONE = isDisputedSession
    ? "cancelled"
    : isRetroResultEntry
      ? event.scoreVerificationStatus === "verified"
        ? "completed"
        : event.scoreVerificationStatus === "pending" ||
            event.scoreVerificationStatus == null
          ? "in_progress"
          : "cancelled"
      : smartTime.icon === "live"
        ? "in_progress"
        : (event.status as keyof typeof STATUS_TONE);
  const statusPill = (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold",
        STATUS_TONE[statusTone] ?? STATUS_TONE.upcoming,
      )}
    >
      {smartTime.icon === "live" && !isRetroResultEntry ? (
        <span className="size-1.5 animate-pulse rounded-full bg-current motion-reduce:animate-none" />
      ) : statusTone === "completed" ? (
        <CheckCircle2 className="size-3" />
      ) : statusTone === "cancelled" ? (
        <AlertTriangle className="size-3" />
      ) : (
        <Clock className="size-3" />
      )}
      {statusBadgeLabel}
    </span>
  );

  useEffect(() => {
    setShareCount(event.shareCount);
  }, [event.shareCount]);

  const handleToggleLike = () => {
    const previousLiked = isLiked;
    const previousCount = likeCount;

    // Optimistic update
    setIsLiked(!isLiked);
    setLikeCount((prev) => prev + (isLiked ? -1 : 1));

    toggleLikeMutation.mutate(
      { activitySessionId: event.id, currentlyLiked: previousLiked },
      {
        onError: () => {
          // Rollback on error
          setIsLiked(previousLiked);
          setLikeCount(previousCount);
          toast.error("Couldn't update like right now");
        },
      },
    );
  };

  const handleOpenComments = () => {
    setCommentsOpen(true);
  };

  const handleOpenLikes = () => {
    if (likeCount === 0) return;
    setLikesOpen(true);
  };

  const handleCommentCountChange = (count: number) => {
    setCommentCount(count);
  };

  const getShareUrl = () => absolutePublicAppUrl(sessionHref);

  const recordShare = (
    target: "system" | "copy" | "chat" | "native" | "story",
  ) => {
    trackShareMutation.mutate(
      { entityType: "activity_session", entityId: event.id, target },
      {
        onSuccess: (result) => {
          setShareCount(result.shareCount);
        },
      },
    );
  };

  const handleOpenQuote = () => {
    setActionsOpen(false);
    setQuoteOpen(true);
  };

  const handleOpenSendInChat = () => {
    setActionsOpen(false);
    setSendInChatOpen(true);
  };

  const handleOpenDeleteConfirm = () => {
    setActionsOpen(false);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteFromFeed = () => {
    deleteFeedItemMutation.mutate(
      { entityType: "activity_session", entityId: event.id },
      {
        onSuccess: () => {
          toast.success("Removed from feed");
          setDeleteConfirmOpen(false);
        },
        onError: (error) => {
          toast.error(
            error instanceof Error
              ? error.message
              : "Couldn't remove this feed item",
          );
        },
      },
    );
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(getShareUrl());
      recordShare("copy");
      toast.success("Link copied");
      setActionsOpen(false);
    } catch (_error) {
      toast.error("Couldn't copy link");
    }
  };

  const handleShare = () => {
    setActionsOpen(false);
    setSendInChatOpen(true);
  };

  const handleCardClick = (clickEvent: React.MouseEvent<HTMLElement>) => {
    const target = clickEvent.target as HTMLElement | null;
    if (
      target?.closest(
        "a, button, input, textarea, select, [role='button'], [data-no-card-nav='true']",
      )
    ) {
      return;
    }

    // Same contract as a BackAwareLink tap: stamp where we came from so the
    // session's back button returns to the feed rather than its fallback, and
    // snapshot the scroll so it returns to this card rather than the top.
    captureCurrentRouteScroll();
    router.push(sessionHrefWithFrom);
  };

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: the card delegates pointer navigation while preserving nested buttons and links
    <article
      className={cn(
        "border-b border-border bg-background",
        embedded && "border-b-0",
      )}
      onClick={handleCardClick}
    >
      {/* Instagram-style Header */}
      <FeedCardHeader
        layout={embedded ? "row" : "x"}
        displayName={event.creator.displayName || ""}
        handle={creatorHandle}
        avatarUrl={event.creator.avatarUrl}
        createdAt={new Date(event.createdAt)}
        timeAgo={timeAgo}
        isBlocked={isCreatorBlocked}
        extraBadge={
          isDisputedSession ? (
            <span className="font-medium text-error">Disputed result</span>
          ) : null
        }
      />
      <div className={embedded ? undefined : FEED_X_BODY}>
        {/* Text leads the card (X/Twitter-style), two lines behind "more". */}
        {showDescription && event.description ? (
          <div className="px-3 pt-1 pb-2">
            <ExpandablePostText
              content={event.description}
              clampLines={5}
              textClassName="text-[15px]"
            />
          </div>
        ) : null}

        {/* One rounded card in the post column: the activity scene on top,
            then the /sessions/[id] hero in miniature — activity tile, eyebrow,
            title, calendar tile — and the facts. */}
        <div className="px-3 pt-1 pb-1.5">
          <div className="relative isolate overflow-hidden rounded-2xl border border-border bg-surface dark:border-white/[0.1] dark:bg-white/[0.03]">
            {/* Every session leads with its scene — see SessionScene. */}
            <div className="relative border-b border-border dark:border-white/[0.08]">
              <SessionScene
                activityId={event.activityId}
                activityIcon={event.activityIcon}
                sessionFormat={event.sessionFormat}
                scoreData={scoreData}
                participants={event.participants.map((p) => ({
                  id: p.id,
                  displayName: p.displayName,
                  avatarUrl: p.avatarUrl,
                  username: p.username,
                  team: p.team as "team1" | "team2" | null,
                }))}
              />
            </div>

            <div className="p-3.5">
              <div className="flex items-start gap-3">
                <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-brand/10 text-brand dark:bg-brand/20 dark:text-violet-200 [&_svg]:size-6">
                  {IconComponent ? <IconComponent /> : <Activity />}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[11px] font-medium tracking-wide text-text-secondary uppercase">
                    {event.activityName}
                    {isRetroResultEntry
                      ? " · Result"
                      : ` · ${VISIBILITY_LABEL[event.visibility] ?? "Session"}`}
                  </p>
                  <BackAwareLink
                    href={sessionHref}
                    className="mt-0.5 line-clamp-2 text-[16px] leading-snug font-bold text-text-primary transition-colors hover:text-brand"
                  >
                    {displayTitle}
                  </BackAwareLink>
                </div>
                <span
                  aria-hidden
                  className="flex w-11 shrink-0 flex-col items-center overflow-hidden rounded-xl border border-border bg-background text-center dark:border-white/[0.12] dark:bg-white/[0.05]"
                >
                  <span className="w-full bg-brand py-px text-[9px] font-semibold tracking-wider text-white uppercase">
                    {formatEventDate(startDate, "MMM")}
                  </span>
                  <span className="pt-1 text-[17px] leading-none font-bold text-text-primary tabular-nums">
                    {formatEventDate(startDate, "d")}
                  </span>
                  <span className="pt-0.5 pb-1 text-[9px] font-medium text-text-tertiary uppercase">
                    {formatEventDate(startDate, "EEE")}
                  </span>
                </span>
              </div>

              <div className="mt-3 space-y-1.5 text-[13px] text-text-secondary">
                <p className="flex min-w-0 items-center gap-2">
                  <Clock className="size-3.5 shrink-0 text-text-tertiary" />
                  <span className="truncate">{timeRangeLabel}</span>
                </p>
                {hasDisplayLocation ? (
                  <p className="flex min-w-0 items-center gap-2">
                    <MapPin className="size-3.5 shrink-0 text-text-tertiary" />
                    <span className="truncate">
                      {event.locationName || event.address}
                    </span>
                  </p>
                ) : null}
              </div>

              <div className="mt-3 flex min-w-0 items-center gap-2">
                {event.participants.length > 0 ? (
                  <span className="flex shrink-0 -space-x-1.5">
                    {event.participants.slice(0, 4).map((p, i) => (
                      <Avatar
                        key={p.id}
                        className="size-6 border-2 border-surface dark:border-black"
                        style={{ zIndex: 10 - i }}
                      >
                        <AvatarImage src={p.avatarUrl ?? undefined} />
                        <AvatarFallback className="rounded-none bg-surface-alt text-[8px]">
                          {p.displayName.slice(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                    ))}
                  </span>
                ) : (
                  <Users className="size-3.5 shrink-0 text-text-tertiary" />
                )}
                <span className="min-w-0 truncate text-[13px] text-text-secondary">
                  <span className="font-semibold text-text-primary tabular-nums">
                    {event.participantCount}
                  </span>{" "}
                  of {event.maxParticipants} {isUpcoming ? "going" : "joined"}
                  {isUpcoming && spotsLeft > 0 ? (
                    <span className="text-text-tertiary">
                      {" "}
                      · {spotsLeft} left
                    </span>
                  ) : null}
                </span>
                <span className="ml-auto">{statusPill}</span>
              </div>

              {event.pricingType === "paid" || skillRequirementLabel ? (
                <div className="mt-2.5 flex flex-wrap gap-1.5">
                  {event.pricingType === "paid" ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-brand/10 px-2 py-0.5 text-[11px] font-medium text-brand dark:text-violet-200">
                      <Wallet className="size-3" />
                      {priceLabel}
                    </span>
                  ) : null}
                  {skillRequirementLabel ? (
                    <span
                      className={cn(
                        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium",
                        event.isViewerSkillLevelEligible === false
                          ? "bg-error/10 text-error"
                          : "bg-surface-alt text-text-secondary dark:bg-white/[0.06]",
                      )}
                    >
                      <Gauge className="size-3" />
                      {skillRequirementLabel}
                    </span>
                  ) : null}
                </div>
              ) : null}

              {/* Only a call to action when there is one to take. */}
              {showCta ? (
                <Button
                  size="sm"
                  asChild
                  className={cn(
                    "mt-3 h-9 w-full rounded-xl text-[13px] font-semibold",
                    canJoinDirect || canRequestToJoin
                      ? "bg-brand text-white hover:bg-brand-hover dark:bg-brand dark:hover:bg-brand-hover"
                      : hasPendingRequest
                        ? "bg-warning/15 text-warning hover:bg-warning/25 dark:bg-warning/20"
                        : "border border-border bg-transparent text-text-primary hover:bg-surface-alt dark:border-white/15 dark:bg-transparent dark:hover:bg-white/[0.06]",
                  )}
                >
                  <BackAwareLink href={ctaHref}>{ctaLabel}</BackAwareLink>
                </Button>
              ) : null}
            </div>
          </div>
        </div>

        {!embedded ? (
          <FeedActionRow
            layout="x"
            className="px-3"
            likeCount={likeCount}
            commentCount={commentCount}
            shareCount={shareCount}
            isLiked={isLiked}
            onLike={handleToggleLike}
            onOpenLikes={handleOpenLikes}
            onComment={handleOpenComments}
            onQuote={handleOpenQuote}
            onShare={handleShare}
            onMore={() => setActionsOpen(true)}
            likeDisabled={toggleLikeMutation.isPending}
            shareDisabled={trackShareMutation.isPending}
          />
        ) : null}

        <Drawer open={isActionsOpen} onOpenChange={setActionsOpen} modal>
          <DrawerContent className="border-t border-border outline-none">
            <DrawerTitle className="sr-only">Session actions</DrawerTitle>
            <div className="mx-auto w-full max-w-lg p-4 pb-[max(env(safe-area-inset-bottom),1rem)]">
              <div className="flex flex-col gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  className="h-12 w-full justify-start rounded-xl px-4 text-text-primary"
                  onClick={handleShare}
                >
                  <Share2 className="h-5 w-5" />
                  Share
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="h-12 w-full justify-start rounded-xl px-4 text-text-primary"
                  onClick={handleOpenSendInChat}
                >
                  <Send className="h-5 w-5" />
                  Send in chat
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="h-12 w-full justify-start rounded-xl px-4 text-text-primary"
                  onClick={handleCopyLink}
                >
                  <Link2 className="h-5 w-5" />
                  Copy link
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="h-12 w-full justify-start rounded-xl px-4 text-text-primary"
                  onClick={handleOpenQuote}
                >
                  <Quote className="h-5 w-5" />
                  Quote repost
                </Button>
                {!isOwner ? (
                  <Button
                    type="button"
                    variant="ghost"
                    className="h-12 w-full justify-start rounded-xl px-4 text-text-primary"
                    onClick={() => {
                      setActionsOpen(false);
                      setReportOpen(true);
                    }}
                  >
                    <Flag className="h-5 w-5" />
                    Report
                  </Button>
                ) : null}
                {isOwner ? (
                  <Button
                    type="button"
                    variant="ghost"
                    className="h-12 w-full justify-start rounded-xl px-4 text-destructive"
                    onClick={handleOpenDeleteConfirm}
                    disabled={deleteFeedItemMutation.isPending}
                  >
                    <Trash2 className="h-5 w-5" />
                    Delete from feed
                  </Button>
                ) : null}
              </div>
              <Button
                type="button"
                variant="outline"
                className="mt-3 h-12 w-full rounded-xl"
                onClick={() => setActionsOpen(false)}
              >
                Cancel
              </Button>
            </div>
          </DrawerContent>
        </Drawer>

        <CommentsSheet
          entityType="activity-session"
          entityId={event.id}
          open={isCommentsOpen}
          onOpenChange={setCommentsOpen}
          viewer={viewer}
          canModerate={isOwner}
          onCommentCountChange={handleCommentCountChange}
        />

        <LikesSheet
          entityType="activity-session"
          entityId={event.id}
          open={isLikesOpen}
          onOpenChange={setLikesOpen}
          currentUserId={viewer?.id}
        />

        {!isOwner ? (
          <ReportFeedItemDialog
            open={isReportOpen}
            onOpenChange={setReportOpen}
            targetUserId={event.creator.id}
            targetLabel="session"
            entityType="activity_session"
            entityId={event.id}
          />
        ) : null}
        {isQuoteOpen ? (
          <RegularPostDrawer
            open={isQuoteOpen}
            onOpenChange={setQuoteOpen}
            currentUser={{
              id: viewer?.id ?? "",
              displayName: viewer?.displayName ?? "You",
              avatarUrl: viewer?.avatarUrl ?? null,
            }}
            quoteTarget={{
              kind: "feed_entity",
              target: quoteTarget,
              defaultVisibility: "private",
            }}
            onSuccess={() => setQuoteOpen(false)}
          />
        ) : null}
        <Dialog open={isDeleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete from feed?</DialogTitle>
              <DialogDescription>
                This removes the session post from your feed without deleting
                the underlying session, participants, chat, or score history.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDeleteConfirmOpen(false)}
                disabled={deleteFeedItemMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={handleDeleteFromFeed}
                disabled={deleteFeedItemMutation.isPending}
              >
                {deleteFeedItemMutation.isPending ? "Deleting..." : "Delete"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <SendToChatDrawer
          open={isSendInChatOpen}
          onOpenChange={setSendInChatOpen}
          payload={{
            type: "entity",
            entityType: "activity_session",
            entityId: String(event.id),
          }}
          nativeShare={{
            title: event.title,
            text: `${event.activityName} on Hiive`,
            url: getShareUrl(),
          }}
          onShared={recordShare}
        />
      </div>
    </article>
  );
}
